import { useCallback, useState, useRef } from 'react';
import {
  getAttachmentPresignedUrls,
  uploadFileToPresignedUrl,
} from '../../services/Feed.service';
import {
  createAttachUploadPayload,
  createAttachmentUploadItem,
  validateAttachment,
  getFileSize,
  safeCreateObjectURL,
} from '../../mappers/feedUtility';
import { ATTACHMENT_FLOW_STATUS } from '../../constants';
import { getValidationErrorMessage } from '../../../../../util/APIUtils';

const DEFAULT_SNACKBAR = { show: false, message: '' };

const clearFileInput = (inputElement) => {
  if (inputElement) inputElement.value = '';
};

const getUploadErrorMessage = (error, fileName) => getValidationErrorMessage(error?.validationError) || `Upload failed for "${fileName || 'file'}".`;

/**
 * Attachment upload hook for Feeds module.
 * Keeps upload state and validation local while exposing only what callers need.
 */
export const useAttachmentUploadHook = (options = {}) => {
  const { rules, onUploadSuccess, onUploadError } = options;
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [snackbarError, setSnackbarError] = useState(DEFAULT_SNACKBAR);
  const fileInputRef = useRef(null);

  const updateAttachmentFileById = useCallback((fileId, updates) => {
    setAttachmentFiles((prev) =>
      prev.map((file) => (file.fileId !== fileId ? file : { ...file, ...updates }))
    );
  }, []);

  const handleAttachmentOnClick = useCallback((fileDetails) => {
    updateAttachmentFileById(fileDetails?.fileId, {
      isModalOpen: true,
      onModalClose: () =>
        updateAttachmentFileById(fileDetails?.fileId, { isModalOpen: false }),
    });
  }, [updateAttachmentFileById]);

  const handleAttachmentOnRemove = useCallback((fileDetails) => {
    setAttachmentFiles((prev) => prev.filter((f) => f.fileId !== fileDetails?.fileId));
  }, []);

  const buildAttachmentItemWithHandlers = useCallback((selectedFile) => {
    const baseItem = createAttachmentUploadItem(selectedFile);
    if (!baseItem) return null;
    return {
      ...baseItem,
      onRemove: (fileDetails) => {
        handleAttachmentOnRemove(fileDetails);
      },
      onClick: (fileDetails) => {
        handleAttachmentOnClick(fileDetails);
      },
    };
  }, [handleAttachmentOnClick, handleAttachmentOnRemove]);

  const getPresignedUrlForUploadingFile = useCallback(async (uploadItem) => {
    const payload = createAttachUploadPayload(uploadItem);
    const response = await getAttachmentPresignedUrls(payload ? [payload] : []);
    if (response?.code === 'VALIDATION_ERROR') {
      const err = new Error('Validation failed');
      err.validationError = response;
      throw err;
    }
    const uploadUrls = response?.data?.uploadUrls || [];
    const uploadData =
      uploadUrls.find((item) => item.fileId === uploadItem.fileId) || uploadUrls[0];
    if (!uploadData?.presignedUrl || !uploadData?.s3Key) {
      throw new Error('Failed to prepare file upload.');
    }
    return {
      presignedUrl: uploadData.presignedUrl,
      s3Key: uploadData.s3Key,
      expiresAt: uploadData.expiresAt || '',
    };
  }, []);

  const fetchPresignedAndSetLoading = useCallback(
    async (uploadItem, fileId) => {
      const presignedData = await getPresignedUrlForUploadingFile(uploadItem);
      updateAttachmentFileById(fileId, {
        ...presignedData,
        isLoading: true,
        disabled: true,
      });
      return presignedData;
    },
    [getPresignedUrlForUploadingFile, updateAttachmentFileById]
  );

  const performUploadToPresignedUrl = useCallback(
    async (presignedUrl, file) => {
      const uploadResponse = await uploadFileToPresignedUrl({
        presignedUrl,
        file,
        mimeType: file.type,
      });
      if (!uploadResponse?.ok && uploadResponse?.status !== 200) {
        throw new Error(`Upload failed for "${file?.name || 'file'}".`);
      }
    },
    []
  );

  const applyUploadSuccessState = useCallback(
    (fileId, selectedFile, fileUrl, presignedData) => {
      updateAttachmentFileById(fileId, {
        uploadStatus: ATTACHMENT_FLOW_STATUS.SUCCESS,
        loading: false,
        disabled: false,
        hasError: false,
        errorMessage: '',
        file: null,
        fileUrl,
        metadata: getFileSize(selectedFile.size),
      });
      if (typeof onUploadSuccess === 'function') {
        onUploadSuccess({ fileId, file: selectedFile, fileUrl, presignedData });
      }
    },
    [updateAttachmentFileById, onUploadSuccess]
  );

  const applyUploadErrorState = useCallback(
    (fileId, error, selectedFile) => {
      const snackbarMessage = getUploadErrorMessage(error, selectedFile?.name);
      setSnackbarError({ show: true, message: snackbarMessage });
      updateAttachmentFileById(fileId, {
        uploadStatus: ATTACHMENT_FLOW_STATUS.ERROR,
        loading: false,
        disabled: false,
        hasError: true,
        errorMessage: 'Failed to upload',
      });
      if (typeof onUploadError === 'function') {
        onUploadError(error);
      }
    },
    [updateAttachmentFileById, onUploadError]
  );

  const handleFileChange = useCallback(
    async (event) => {
      const inputElement = event?.target;
      const selectedFile = inputElement?.files?.[0] ?? null;

      const validationMessage = validateAttachment(
        selectedFile,
        attachmentFiles.length,
        rules
      );
      if (validationMessage) {
        setSnackbarError({ show: true, message: validationMessage });
        clearFileInput(inputElement);
        return;
      }

      const uploadItem = buildAttachmentItemWithHandlers(selectedFile);
      if (!uploadItem) {
        clearFileInput(inputElement);
        return;
      }
      setAttachmentFiles((prev) => [...prev, uploadItem]);
      const fileId = uploadItem.fileId;

      try {
        const presignedData = await fetchPresignedAndSetLoading(uploadItem, fileId);
        await performUploadToPresignedUrl(presignedData.presignedUrl, selectedFile);
        const fileUrl = safeCreateObjectURL(selectedFile);
        applyUploadSuccessState(fileId, selectedFile, fileUrl, presignedData);
      } catch (error) {
        applyUploadErrorState(fileId, error, selectedFile);
      } finally {
        clearFileInput(inputElement);
      }
    },
    [
      attachmentFiles.length,
      rules,
      buildAttachmentItemWithHandlers,
      fetchPresignedAndSetLoading,
      performUploadToPresignedUrl,
      applyUploadSuccessState,
      applyUploadErrorState,
    ]
  );

  const onFileUploadClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  }, []);

  const onSnackbarClose = useCallback(() => {
    setSnackbarError(DEFAULT_SNACKBAR);
  }, []);

  const resetAttachments = useCallback(() => {
    setAttachmentFiles([]);
    setSnackbarError(DEFAULT_SNACKBAR);
  }, []);

  return {
    attachmentFiles,
    snackbarError,
    fileInputRef,
    handleFileChange,
    onFileUploadClick,
    onSnackbarClose,
    resetAttachments,
  };
};
